// Trade share URL builders. Two URLs:
//
//   1. **Share URL** — `/trade/<signature>?m=&u=&d=&a=&o=`. Short,
//      OG-friendly. The `app/[locale]/trade/[signature]/page.tsx` route
//      owns this URL, emits OG metadata for crawlers, and bounces the
//      user to the canonical market URL on the client.
//   2. **Canonical URL** — `/markets/<marketPubkey>`. The actual page
//      where the user lands after the share-link redirect.
//
// Trade context (direction, amount, outcome, market, user) is encoded
// in the share URL's query string because no `getTrade(signature)` SDK
// endpoint exists today — the page reads its own search params and
// passes them through to `buildOgMetadata`.

const TRADE_SHARE_PATH_PREFIX = '/trade';
const MARKETS_PATH_PREFIX = '/markets';

export interface BuildTradeShareUrlContext {
  marketPubkey: string;
  userPubkey: string;
  direction: 'buy' | 'sell' | 'swap';
  /** D6-encoded USDC amount as a string (BigInt-safe). */
  amountD6: string;
  outcomeIndex: number;
}

/** Relative share-link path with trade context encoded as query params.
 *  Pair with `buildOgMetadata({ kind: 'trade', ... })` so the OG image
 *  receives the same context. */
export function buildTradeShareUrl(signature: string, ctx: BuildTradeShareUrlContext): string {
  const qs = new URLSearchParams({
    m: ctx.marketPubkey,
    u: ctx.userPubkey,
    d: ctx.direction,
    a: ctx.amountD6,
    o: ctx.outcomeIndex.toString(),
  });
  return `${TRADE_SHARE_PATH_PREFIX}/${signature}?${qs.toString()}`;
}

/** Relative canonical path the share page redirects to. */
export function buildTradeCanonicalUrl(marketPubkey: string): string {
  return `${MARKETS_PATH_PREFIX}/${marketPubkey}`;
}
